/* eslint-disable -- vendored file, it originally had no types and a different eslint config */
/**
 * todo(calebbarnes): this vendored file is a copy of the Select component from netlify-react-ui
 * todo: It has been modified to be wrapped with forwardRef
 */
import { forwardRef, type RefCallback } from "react";
import type {
  ActionMeta,
  GroupBase,
  InputActionMeta,
  Props,
  PropsValue,
  SelectInstance,
} from "react-select";
import ReactSelect, { components } from "react-select";
import type { AsyncProps } from "react-select/async";
import Async from "react-select/async";
import type { CreatableProps } from "react-select/creatable";
import Creatable from "react-select/creatable";

import selectStyles from "./Select.styles";
import { DecorativeIcon } from "../../Icon/DecorativeIcon";
/* We use custom styles in forms.css to style the ReactSelect. */

function trim(value?: any) {
  return value?.trim ? value?.trim?.() : value;
}

const isUnique =
  (isValidNewOption: any) => (inputValue: any, option: any, options: any) => {
    const value = trim(inputValue);
    const isValid = isValidNewOption
      ? isValidNewOption(inputValue, option, options)
      : true;
    return isValid && !!value && !options.find((o: any) => o.value === value);
  };

function normalizeOption({
  option,
  valueOnly,
  isMulti,
}: {
  option: any;
  valueOnly: any;
  isMulti: any;
}) {
  if (!option) {
    return valueOnly && isMulti ? [] : null;
  }
  if (option.map) {
    return option.map((o: any) =>
      normalizeOption({ option: o, valueOnly, isMulti }),
    );
  }
  if (typeof option === typeof {}) {
    option.value = trim(option.value);
    return valueOnly ? option.value : option;
  }
  return option;
}

const getOptionByValue = (options: any, value: any) =>
  options.find((option: any) => option.value === value);

function normalizeValue(value: any, options: any) {
  if (!value || !options) {
    return value;
  }
  if (typeof value === "string") {
    return getOptionByValue(options, value);
  }

  if (value.map && typeof value[0] === "string") {
    return value.map((v: any) => getOptionByValue(options, v));
  }

  return value;
}

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <DecorativeIcon name="caret-down" />
  </components.DropdownIndicator>
);

const ClearIndicator = (props: any) => (
  <components.ClearIndicator {...props}>
    <DecorativeIcon name="close" data-testid="react-select-clear-indicator" />
  </components.ClearIndicator>
);

const Placeholder = (props: any) => (
  <components.Placeholder {...props}>
    {props.selectProps.isLoading ? "Loading..." : props.children}
  </components.Placeholder>
);

const Input = (props: any) => (
  <components.Input
    {...props}
    {...props.selectProps.inputProps}
    required={props.selectProps.required && !props.hasValue}
  />
);

const LoadingIndicator = () => (
  <DecorativeIcon
    name="gear"
    className="!tw-text-textMuted motion-safe:tw-animate-rotate-loop"
  />
);

const MenuList = (props: any) => (
  <components.MenuList {...props}>
    {props.children}
    {props.isLoading && (
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2">
        <LoadingIndicator />
        <span className="tw-text-textMuted">
          {props.selectProps.loadingMessage()}
        </span>
      </div>
    )}
  </components.MenuList>
);

const LoadingMessage = () => null;

type SelectAsyncCreatableProps<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = Props<Option, IsMulti, Group> &
  AsyncProps<Option, IsMulti, Group> &
  CreatableProps<Option, IsMulti, Group>;

export type SelectProps<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = Omit<
  SelectAsyncCreatableProps<Option, IsMulti, Group>,
  "value" | "onChange"
> & {
  value?: PropsValue<unknown>;
  creatable?: CreatableProps<Option, IsMulti, Group>;
  valueOnly?: boolean;
  outset?: string;
  hasSearchStyles?: boolean;
  maxLength?: number;
  onChange?: (
    e: { target: { name: string; value: unknown } } | PropsValue<Option>,
  ) => void;
  inputProps?: React.HTMLProps<HTMLInputElement>;
  refCallback?: RefCallback<SelectInstance<Option, IsMulti, Group>>;
};

//@ts-expect-error
const Select = forwardRef(function Select<
  Option,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  {
    closeMenuOnSelect = true,
    components = {},
    creatable,
    createOptionPosition = "first",
    hasSearchStyles = false,
    id,
    inputProps = {},
    isClearable = false,
    isSearchable = true,
    loadOptions,
    maxLength,
    name,
    noOptionsMessage = () => "No results found",
    onChange,
    openMenuOnClick = true,
    outset = "",
    refCallback,
    styles,
    value,
    valueOnly = false,
    ...props
  }: any,
  ref: SelectProps<Option, IsMulti, Group>,
) {
  const handleChange = (option: any, _actionMeta: ActionMeta<Option>) => {
    const value = normalizeOption({
      option,
      valueOnly,
      isMulti: props.isMulti,
    });
    onChange?.(name ? { target: { name, value } } : value);
  };

  const { isValidNewOption } = creatable || {};

  const commonProps: SelectAsyncCreatableProps<Option, IsMulti, Group> = {
    backspaceRemovesValue: true,
    className: outset,
    onInputChange: (inputValue: string, actionMeta: InputActionMeta) => {
      const potentiallyTruncatedInputValue =
        !maxLength || inputValue.length <= maxLength
          ? inputValue
          : inputValue.substring(0, maxLength);
      if (props.onInputChange) {
        props.onInputChange(potentiallyTruncatedInputValue, actionMeta);
      }
      return potentiallyTruncatedInputValue;
    },
    onChange: handleChange,
    openMenuOnClick,
    value: normalizeValue(value, props.options),
    noOptionsMessage,
    inputId: id,
    isClearable,
    isSearchable,
    styles: styles || selectStyles(hasSearchStyles),
    // @ts-expect-error the only way to pass these to the Input component
    // without breaking seemingly random tests is to shove it onto the common props
    // that make it through as `props.selectProps`. please forgive me.
    inputProps,
    closeMenuOnSelect,
    components: {
      DropdownIndicator,
      ClearIndicator,
      Placeholder,
      Input,
      LoadingIndicator,
      MenuList,
      LoadingMessage,
      ...components,
    },
  };

  return creatable ? (
    <Creatable
      ref={ref || refCallback}
      createOptionPosition={createOptionPosition}
      {...creatable}
      {...props}
      isValidNewOption={isUnique(isValidNewOption)}
      {...commonProps}
    />
  ) : loadOptions ? (
    <Async
      ref={ref || refCallback}
      loadOptions={loadOptions}
      {...props}
      {...commonProps}
    />
  ) : (
    <ReactSelect ref={ref || refCallback} {...props} {...commonProps} />
  );
});

export default Select;
